import React, {
    useContext, useCallback, useState, useEffect, useMemo
} from 'react';
import { useLocation } from 'react-router-dom';
import _ from 'lodash';
import { ViewModelServiceContext, ViewModelForm } from 'gw-portals-viewmodel-react';
import PropTypes from 'prop-types';
import { useValidation } from 'gw-portals-validation-react';
import htmlParser from 'html-react-parser';
import { LoadSaveService } from 'gw-capability-quoteandbind';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import SgProductService from 'sg-product-service';
import {
    SgSessionStorageService, SgLoadingContext, ADOBE_TOPICS, COMMON_TAGGING_PROPERTIES, SgSessionService
} from 'saga-modules';
import { useSgAdobeAnalyticsSmiIpidAndRetrieval } from 'sg-adobe-analytics';
import SgBreadcrumbs from 'sg-portals-wizard-react/Wizard/components/sg-breadcrumbs/SgBreadcrumbs';
import SgAggregatorService from 'sg-aggregator/SgAggregatorService';
import CommonConstants from 'sg-common-constants';
import IndicativeToFullClearingData from 'sg-indicative-clearing-data';
import { SgDate, SgDateInputTypes } from 'sg-date';
import wizardStyles from 'sg-portals-wizard-react/Wizard/templates/SgWizardPageTemplate.module.scss';
import SgQqWhitelistUtil from 'sg-qq-whitelist-util';
import { publish } from '@jutro/events';
import { SG_AA_RETRIEVAL_FAILURES } from 'sg-adobe-analytics/AdobeAnalyticsConstants';
import SgQnBLobSpecificUtil from 'sg-qnb-lob-specific-util';
import messages from './RetrievalPage.messages';
import metadata from './RetrievalPage.metadata.json5';

function RetrievalPage(props) {
    const [retrieveQuoteVM, updateRetrieveQuoteVM] = useState({ dateOfBirth: { aspects: true } });
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'tablet' || breakpoint === 'phoneWide' || breakpoint === 'phone';
    const [contactUsPhoneNumber, setContactUsPhoneNumber] = useState(CommonConstants.SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_COMMON_DEFAULT_PHONE_NUMBER);
    const { setShowLoadingModal } = useContext(SgLoadingContext);
    const [showErrors, setShowErrors] = useState(false);
    const [appErrorCode, setAppErrorCode] = useState(-1);
    const [appErrorReason, setAppErrorReason] = useState(-1);
    const [customError, setCustomError] = useState('');
    const [invalidProductRetrieval, setInvalidProductRetrieval] = useState(false);
    const { search } = useLocation();
    const [aggDetails, setAggDetails] = useState(null);
    const query = new URLSearchParams(search);
    const gid = query.get('gid');
    const qqId = query.get('qqId');
    const encodedQqId = encodeURIComponent(qqId); // special signs got decoded when reading URL params, so we need to encode it again to avoid errors
    const pn = query.get('pn') ? encodeURIComponent(query.get('pn')) : undefined;
    const [isLoading, setIsLoading] = useState(true);

    const {
        onValidate
    } = useValidation('RetrievalPage');
    useSgAdobeAnalyticsSmiIpidAndRetrieval();
    SgSessionStorageService.removeItem(SgSessionStorageService.KEY.LAST_REQUEST_TIME);
    SgSessionStorageService.removeItem(SgSessionStorageService.KEY.TIMEOUT);
    const readOnlySteps = useMemo(() => {
        if (SgProductService.isSmi()) {
            return [
                messages.RetrievalPageAboutYouStep,
                messages.RetrievalPageQuoteStep,
                messages.RetrievalPageReviewStep,
                messages.RetrievalPagePaymentStep,
                messages.RetrievalPageConfirmationStep,
            ];
        }
        if (SgProductService.isTravel()) {
            return [
                messages.RetrievalPageDetailsStep,
                messages.RetrievalPageMedicalStep,
                messages.RetrievalPageQuoteStep,
                messages.RetrievalPagePaymentStep,
                messages.RetrievalPageConfirmationStep,
            ];
        }
        return [SgProductService.coverableName(),
            SgProductService.isHome() ? messages.RetrievalPageDetailsStep : messages.RetrievalPageDriversStep,
            messages.RetrievalPageQuoteStep,
            messages.RetrievalPagePaymentStep,
            messages.RetrievalPageConfirmationStep
        ];
    }, []);

    const retrievalErrorCode = useMemo(() => {
        let retrievalErrorMessage;
        switch (appErrorCode) {
            case CommonConstants.SG_ERROR_CODES.SAGA_SESSION_BLOCKED_ERROR:
                setContactUsPhoneNumber(CommonConstants.SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_COMMON_DEFAULT_PHONE_NUMBER);
                retrievalErrorMessage = messages.RetrievalPageLockedError;
                publish(ADOBE_TOPICS.ADOBE_EVENT,
                    { adobeID: COMMON_TAGGING_PROPERTIES.retrievalFailure, value: SG_AA_RETRIEVAL_FAILURES.LOCKED_OUT });
                break;
            case CommonConstants.SG_ERROR_CODES.SAGA_RETRIEVING_AMENDED_OFFLINE:
            case CommonConstants.SG_ERROR_CODES.SAGA_CANNOT_RETRIEVE_ONLINE:
                setContactUsPhoneNumber(CommonConstants.SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_COMMON_DEFAULT_PHONE_NUMBER);
                retrievalErrorMessage = messages.RetrievalPageChangedOfflineError;
                if (customError.includes(CommonConstants.PRODUCT_NO_LONGER_RETRIEVABLE)) {
                    setInvalidProductRetrieval(true);
                    retrievalErrorMessage = SgQnBLobSpecificUtil.getRetrievalPageCustomErrorMessageContent(customError, translator);
                }
                publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: COMMON_TAGGING_PROPERTIES.retrievalFailure, value: SG_AA_RETRIEVAL_FAILURES.OFFLINE });
                break;
            case CommonConstants.SG_ERROR_CODES.SAGA_EXPIRED_QUOTE_RETRIEVAL_ERROR:
                retrievalErrorMessage = messages.RetrievalPageExpiredError;
                publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: COMMON_TAGGING_PROPERTIES.retrievalFailure, value: SG_AA_RETRIEVAL_FAILURES.EXPIRED });
                break;
            case CommonConstants.SG_ERROR_CODES.SAGA_RETRIEVING_INVALID_COVER:
                setContactUsPhoneNumber(CommonConstants.SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_MOTOR_RETRIEVE_PHONE_NUMBER);
                retrievalErrorMessage = messages.RetrievalPageInvalidCoverError;
                publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: COMMON_TAGGING_PROPERTIES.retrievalFailure, value: SG_AA_RETRIEVAL_FAILURES.INVALID });
                break;
            case CommonConstants.SG_ERROR_CODES.SAGA_DECLINE:
                setContactUsPhoneNumber(CommonConstants.SAGA_PHONE_NUMBERS.SAGA_HOME_LANDLORD_PHONE_NUMBER);
                retrievalErrorMessage = SgQnBLobSpecificUtil.getRetrievalPageErrorReasonMessage(appErrorReason);
                publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: COMMON_TAGGING_PROPERTIES.retrievalFailure, value: SG_AA_RETRIEVAL_FAILURES.INVALID });
                break;
            case CommonConstants.SG_ERROR_CODES.SAGA_RETRIEVE_TIA_QUOTE:
                publish(ADOBE_TOPICS.ADOBE_EVENT, {
                    adobeID: COMMON_TAGGING_PROPERTIES.retrievalFailure, value: SG_AA_RETRIEVAL_FAILURES.TIA_NUMBER
                });
                break;
            default:
                retrievalErrorMessage = messages.RetrievalPageWrongDetailsError;
                if (appErrorCode !== -1) {
                    publish(ADOBE_TOPICS.ADOBE_EVENT, {
                        adobeID: COMMON_TAGGING_PROPERTIES.retrievalFailure, value: SG_AA_RETRIEVAL_FAILURES.FAILURE
                    });
                }
        }
        return retrievalErrorMessage;
    }, [appErrorCode, appErrorReason, customError, translator]);

    const retrievePageErrorMessageContent = useMemo(() => {
        return (
            <>
                {translator(retrievalErrorCode, {
                    phoneNumber: isMobile
                        ? <a href={`tel:${contactUsPhoneNumber.replaceAll(' ', '')}`}>{contactUsPhoneNumber}</a>
                        : contactUsPhoneNumber
                })}
            </>
        );
    }, [retrievalErrorCode, isMobile, translator, contactUsPhoneNumber]);

    const newQuoteLink = useMemo(() => {
        const translatedMessage = translator(messages.RetrievalPageNewQuote);
        return appErrorCode === CommonConstants.SG_ERROR_CODES.SAGA_RETRIEVING_AMENDED_OFFLINE
            || appErrorCode === CommonConstants.SG_ERROR_CODES.SAGA_CANNOT_RETRIEVE_ONLINE
            ? translatedMessage : translatedMessage.toLowerCase();
    }, [appErrorCode, translator]);

    const quoteTitle = useMemo(() => {
        if (aggDetails) {
            return translator(messages.RetrievalQuoteSecondTitleBestPrice, {
                aggregatorName: aggDetails.aggregatorName_Ext,
                productName: SgProductService.productName()
            });
        }
        if (!_.isNil(qqId)) {
            return translator(messages.QQRetrievalPageQuoteTitle, {
                productName: SgProductService.productName()
                    .toLowerCase()
            });
        }
        return translator(messages.RetrievalPageQuoteTitle, {
            productName: SgProductService.productName()
                .toLowerCase()
        });
    }, [aggDetails, translator, qqId]);

    const retrieveQuoteDescription = useMemo(() => {
        if (!_.isNil(qqId)) {
            return translator(messages.QQRetrievalPageQuoteDescription);
        }
        if (SgProductService.isSmi()) {
            return translator(messages.SmiRetrievalDescription);
        }
        if (!aggDetails && !pn) {
            return translator(messages.RetrievalPageQuoteDescription);
        }
        const field = translator(SgProductService.isHome() ? messages.RetrieveQuoteEmail : messages.RetrieveQuoteSurname);
        return translator(messages.RetrievalPageQuoteShortDescription, { field: field.toLowerCase() });
    }, [aggDetails, translator, qqId, pn]);

    const productType = useMemo(() => {
        if (SgProductService.isHome()) {
            return translator(messages.RetrievalPageHome);
        }
        if (SgProductService.isSmi()) {
            return translator(messages.RetrievalPageSmi);
        }
        if (SgProductService.isTravel()) {
            return translator(messages.RetrievalPageTravel);
        }
        return translator(messages.RetrievalPageMotor);
    }, []);

    const isBestPriceHomeQuote = useMemo(() => {
        return aggDetails !== null && SgProductService.isHome();
    }, [aggDetails]);

    const shouldShowNewQuoteLink = useMemo(() => {
        return appErrorCode !== -1
            && appErrorCode !== CommonConstants.SG_ERROR_CODES.SAGA_SESSION_BLOCKED_ERROR
            && appErrorCode !== CommonConstants.SG_ERROR_CODES.SAGA_DECLINE
            && invalidProductRetrieval === false;
    }, [appErrorCode, invalidProductRetrieval]);

    const shouldThrowError = useCallback((error) => {
        if (SgProductService.isSmi()) {
            return error.appErrorCode === CommonConstants.SG_ERROR_CODES.SAGA_RETRIEVE_TIA_QUOTE
                || error.appErrorCode === CommonConstants.SG_ERROR_CODES.SAGA_RETRIEVING_AMENDED_OFFLINE;
        }
        if (SgProductService.isTravel()) {
            return error.appErrorCode === CommonConstants.SG_ERROR_CODES.SAGA_RETRIEVE_TIA_QUOTE
                || error.appErrorCode === CommonConstants.SG_ERROR_CODES.SAGA_RETRIEVING_AMENDED_OFFLINE;
        }
        return false;
    }, []);

    const handleSubmissionRetrieve = useCallback((retrieveData) => {
        LoadSaveService.retrieveSubmission(retrieveData)
            .then((submission) => {
                if (_.isEmpty(submission)) {
                    // empty response means submission wasn't found due to incorrect data; throwing error to get correct message displayed
                    const err = { appErrorCode: CommonConstants.SG_ERROR_CODES.GW_ENTITY_NOT_FOUND_ERROR };
                    throw err;
                }
                const wizardLink = '/journey';
                const {
                    location,
                    history
                } = props;
                SgSessionStorageService.setItem(SgSessionStorageService.KEY.LAST_URL, location.pathname);
                SgSessionStorageService.setItem(SgSessionStorageService.KEY.IS_RETRIEVAL, true);
                SgSessionStorageService.setItem(SgSessionStorageService.KEY.IS_RETRIEVAL_JOURNEY, true);
                if (submission.quotationType_Ext === CommonConstants.QUOTATION_TYPE.INDICATIVE_QUOTE) {
                    SgSessionStorageService.setItem(SgSessionStorageService.KEY.PREVENT_SKIP_TO_QUOTE_IN_RETRIEVAL, true);
                    const overrideData = submission.baseData.productCode === CommonConstants.MOTOR_PRODUCT_CODE
                        ? IndicativeToFullClearingData.spm : IndicativeToFullClearingData.sph;
                    const newSubmission = _.merge(submission, overrideData);
                    SgSessionStorageService.setItem(SgSessionStorageService.KEY.MODEL, newSubmission);
                    history.push(wizardLink, {
                        submission: newSubmission
                    });
                } else {
                    SgSessionStorageService.setItem(SgSessionStorageService.KEY.MODEL, submission);
                    SgQnBLobSpecificUtil.setCookiesOnRetrieval(submission);
                    history.push(wizardLink, {
                        submission: submission
                    });
                }
            })
            .catch((error) => {
                setAppErrorCode(error.appErrorCode);
                if (error.baseError && error.baseError.message) {
                    setCustomError(error.baseError.message);
                }
                if (error.baseError && error.baseError.data && error.baseError.data.appData) {
                    setAppErrorReason(error.baseError.data.appData[0]);
                }
                if (shouldThrowError(error)) {
                    throw error;
                }
            })
            .finally(() => {
                setShowLoadingModal(false);
            });
    }, [props, setShowLoadingModal, shouldThrowError]);

    useEffect(() => {
        if (qqId && SgQqWhitelistUtil.isLegalReferrer()) {
            SgSessionService.clearSession();
            setShowLoadingModal(true);
            handleSubmissionRetrieve({ quickQuoteId_Ext: encodedQqId });
            return;
        }

        if (!viewModelService || !SgAggregatorService) {
            return;
        }

        if (gid) {
            setShowLoadingModal(true);
            SgAggregatorService.getAggDetails(gid, productType)
                .then((aggDetailsResponse) => {
                    setAggDetails(aggDetailsResponse);
                    setIsLoading(false);
                })
                .finally(() => {
                    setShowLoadingModal(false);
                });
        } else {
            setIsLoading(false);
        }

        const viewModelContext = qqId
            ? {
                tokenID_Ext: encodedQqId,
                insuranceType_Ext: productType
            }
            : {
                guid_Ext: gid,
                insuranceType_Ext: productType,
                tokenID_Ext: pn
            };

        const retrieveQuoteViewModel = viewModelService.create(
            viewModelContext,
            'pc',
            'edgev11.capabilities.quote.submission.dto.QuoteRetrievalDTO'
        );
        updateRetrieveQuoteVM(retrieveQuoteViewModel);
    }, [viewModelService, SgAggregatorService]);

    useEffect(() => {
        // TESTS PURPOSE ONLY - to fill fields and retrieve quotation quicker with url params, NOT USE ON PROD
        if (!isLoading && (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test')
            && !_.isNil(query.get('id')) && !_.isNil(query.get('dob')) && !_.isNil(query.get('postCode')) && !_.isNil(query.get('lastName'))) {
            const newRetrieveQuoteVM = viewModelService.clone(retrieveQuoteVM);
            _.set(newRetrieveQuoteVM, 'quoteID', query.get('id'));
            _.set(newRetrieveQuoteVM, 'dateOfBirth', JSON.parse(query.get('dob')));
            _.set(newRetrieveQuoteVM, 'postalCode', query.get('postCode'));
            _.set(newRetrieveQuoteVM, 'lastName', query.get('lastName'));
            updateRetrieveQuoteVM(newRetrieveQuoteVM);
        }
    }, [isLoading]);

    const writeValue = useCallback(
        (value, path) => {
            const newRetrieveQuoteVM = viewModelService.clone(retrieveQuoteVM);
            _.set(newRetrieveQuoteVM, path, value);
            updateRetrieveQuoteVM(newRetrieveQuoteVM);
        },
        [retrieveQuoteVM, updateRetrieveQuoteVM, viewModelService]
    );

    const retrieveQuote = useCallback(async () => {
        if (!retrieveQuoteVM.aspects.subtreeValid) {
            setShowErrors(true);
            return;
        }
        setAppErrorCode(-1);
        setShowLoadingModal(true);
        handleSubmissionRetrieve(retrieveQuoteVM.value);
    }, [retrieveQuoteVM, handleSubmissionRetrieve, setShowLoadingModal]);

    const onNewQuote = useCallback(
        () => {
            const { history } = props;
            history.push('/new-quote', {});
        }, []
    );

    const overrideProps = {
        retrievalContainer: {
            className: wizardStyles.digitalWizard
        },
        retrievalForm: {
            className: `${wizardStyles.wizardMain} sgWizardMainLayout`
        },
        sgBreadCrumbs: {
            readOnlySteps: readOnlySteps,
            visible: _.isNil(qqId)
        },
        retrieveQuoteTitleBestPrice: {
            visible: aggDetails !== null
        },
        retrieveQuoteTitle: {
            content: quoteTitle
        },
        retrieveQuoteDescription: {
            content: retrieveQuoteDescription
        },
        retrieveQuoteAdditionalDescriptionBestPrice: {
            visible: aggDetails !== null,
            content: htmlParser(translator(messages.RetrievalQuoteAdditionalDescriptionBestPrice))
        },
        retrieveQuoteReferenceNumber: {
            visible: !aggDetails && !pn && _.isNil(qqId),
            showErrors: showErrors && !retrieveQuoteVM.quoteID.aspects.valid
        },
        retrieveQuoteDateOfBirth: {
            modelVM: retrieveQuoteVM,
            onValidate: onValidate,
            updateData: updateRetrieveQuoteVM,
            datePickerTypeDesktop: SgDateInputTypes.DAY_MONTH_YEAR_INPUTS,
            showErrors: showErrors,
            autoCompleteYear: true
        },
        retrieveQuotePostCode: {
            showErrors: showErrors && !retrieveQuoteVM.postalCode.aspects.valid
        },
        retrieveQuoteSurname: {
            visible: !isBestPriceHomeQuote,
            showErrors: showErrors && !retrieveQuoteVM.lastName.aspects.valid
        },
        retrieveQuoteEmail: {
            visible: isBestPriceHomeQuote,
            showErrors: showErrors && !retrieveQuoteVM.emailAddress_Ext.aspects.valid
        },
        retrievePageErrorMessage: {
            visible: appErrorCode !== -1,
            content: retrievePageErrorMessageContent,
            className: appErrorCode === CommonConstants.SG_ERROR_CODES.SAGA_DECLINE
                || invalidProductRetrieval ? 'retrievalDeclineErrorMessage' : ''
        },
        retrievePageNewQuoteLink: {
            visible: shouldShowNewQuoteLink,
            content: newQuoteLink
        },
        retrieveQuoteInfoBoxFirstParagraph: {
            content: translator(SgProductService.isHome()
                ? messages.RetrievalPageInfoBoxFirstParagraphHome : messages.RetrievalPageInfoBoxFirstParagraphMotor)
        },
        retrievePageInfoBox: {
            visible: SgQnBLobSpecificUtil.showDefaqtoBannerOnRetrievalPage()
        },
        defaqtoLogo: {
            className: SgProductService.isHome() ? 'sgDefaqtoLogoHome' : 'sgDefaqtoLogoMotor'
        },
        retrieveQuoteInfoBoxSecondParagraph: {
            content: translator(SgProductService.isHome()
                ? messages.RetrievalPageInfoBoxSecondParagraphHome : messages.RetrievalPageInfoBoxSecondParagraphMotor)
        },
        retrieveQuoteContinueButton: {
            content: !_.isNil(qqId) ? messages.QQRetrieveButton : messages.RetrieveButton
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            onRetrieveQuote: retrieveQuote,
            onValidate: onValidate,
            onNewQuote: onNewQuote
        },
        resolveComponentMap: {
            SgBreadcrumbs,
            SgDate
        }
    };

    if (isLoading) {
        return '';
    }

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={retrieveQuoteVM}
            overrideProps={overrideProps}
            onModelChange={writeValue}
            classNameMap={resolvers.resolveClassNameMap}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            onValidationChange={onValidate}
        />
    );
}

RetrievalPage.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired
};
export default RetrievalPage;
